/**
 * Strict-bounded highlight-extent extension for PDF citation highlighting.
 *
 * Walks forward from the head item index on the same page, including each
 * subsequent item ONLY if its normalised text appears in the chunk's
 * normalised text from the current chunk position onward. The moment an
 * item isn't found in the chunk (page footer, unrelated section, etc.),
 * the walk stops.
 *
 * Five guards keep the extension strictly bounded:
 *   1. Caller passes only same-page items — never fans across pages
 *   2. Skip items whose normalised text is < 4 chars (single letters,
 *      page numbers, bullet glyphs); keeps content-bearing short words
 *      like "Hazard", "Risk", "PEEP" eligible
 *   3. Item's normalised text must appear in chunkNorm from the current
 *      chunk position onward — the BIG guard. Items not in the chunk
 *      stop the walk.
 *   4. Match position must be within `maxLookAhead` chars of the previous
 *      match position in the chunk — guards against coincidental matches
 *      far ahead in the chunk
 *   5. Hard cap of `maxItems` — defensive against pathological inputs
 *
 * Returns a list of item indices to highlight. The head index is always
 * included; subsequent indices are appended in walk order. Short items
 * (< 4 chars) are skipped silently — they aren't included in the result
 * but they don't stop the walk.
 */

import { normalizeText } from "@/utils/textMatching";

export interface BoundedExtendItem {
  /** Raw text of the pdfjs text item. */
  text: string;
}

export interface BoundedExtendOptions {
  /** Maximum items to include in the highlight (default 40). */
  maxItems?: number;
  /** Items with fewer normalised chars than this are skipped (default 4). */
  minItemLength?: number;
  /** Stop the walk if a match jumps more than this many chars ahead in chunk (default 200). */
  maxLookAhead?: number;
}

export function boundedExtendItems(
  items: BoundedExtendItem[],
  startIndex: number,
  chunkNorm: string,
  options: BoundedExtendOptions = {},
): number[] {
  const { maxItems = 40, minItemLength = 4, maxLookAhead = 200 } = options;

  if (startIndex < 0 || startIndex >= items.length || !chunkNorm) {
    return [];
  }

  const highlighted: number[] = [startIndex];
  const headNorm = normalizeText(items[startIndex].text);
  let lastChunkPos = chunkNorm.indexOf(headNorm);

  // If the head item's normalised text isn't a substring of the chunk
  // (e.g. it was matched fuzzily by the upstream head-matcher), start the
  // walk at chunk position 0 so subsequent items can still be located.
  if (lastChunkPos === -1) {
    lastChunkPos = 0;
  } else {
    lastChunkPos += headNorm.length;
  }

  for (let i = startIndex + 1; i < items.length; i++) {
    const itemNorm = normalizeText(items[i].text);

    if (itemNorm.length < minItemLength) {
      // Guard 2: short items don't stop the walk; they're just not
      // highlighted on their own. The visual highlight skips over them.
      continue;
    }

    const chunkPos = chunkNorm.indexOf(itemNorm, lastChunkPos);

    if (chunkPos === -1) {
      // Guard 3: item not in chunk — stop. Anything beyond this is
      // unrelated content (different section, page footer, etc.).
      break;
    }

    if (chunkPos - lastChunkPos > maxLookAhead) {
      // Guard 4: match is too far ahead — likely coincidental. Stop.
      break;
    }

    highlighted.push(i);
    lastChunkPos = chunkPos + itemNorm.length;

    if (highlighted.length >= maxItems) {
      // Guard 5: hard cap.
      break;
    }
  }

  return highlighted;
}
